import { queryOptions } from '@tanstack/react-query';
import instance from './axios-instance';
import { CommandDashboardTileKey } from '../components/command-dashboard/tile-config';

export const CommandDashboardKeys = {
  ActiveUsersTile: 'CommandDashboardActiveUsersTile',
  RecentAdminActionsTile: 'CommandDashboardRecentAdminActionsTile',
  RulesEngineTile: 'CommandDashboardRulesEngineTile',
  DashboardView: 'CommandDashboardDashboardView',
  TaskStatuses: 'CommandDashboardTaskStatuses',
  LoginActivityTile: 'CommandDashboardLoginActivityTile',
  MasterSchedulerTile: 'CommandDashboardMasterSchedulerTile',
  GroupsAndAccessTile: 'CommandDashboardGroupsAndAccessTile',
};

export const CommandDashboardTaskIds = {
  LoginActivity: 'loginActivity',
  GroupsAndAccess: 'groupsAndAccess',
} as const;

export interface RecentAdminActionEntry {
  adminName: string;
  action: string;
  timestamp: number;
}

export interface CommandDashboardRecentAdminActions {
  entries: RecentAdminActionEntry[];
  nextCursor: string | null;
  totalCount: number;
  todayCount: number;
  yesterdayCount: number;
}

export async function getRecentAdminActions(
  cursor?: string,
  sortDirection?: 'asc' | 'desc'
) {
  const { data } = await instance.get<CommandDashboardRecentAdminActions>(
    '/api/commanddashboard/recentadminactions',
    { params: { cursor, sortDirection } }
  );
  return data;
}

export interface CommandDashboardRulesEngineMetrics {
  activeRules: number;
  draftRules: number;
  rulesTriggeredToday: number;
  rulesWithFailures: number;
  rulesWithErrors: number;
}

export async function getRulesEngineTileMetrics() {
  const res = await instance.get<CommandDashboardRulesEngineMetrics>(
    '/api/rulesEngine/count'
  );
  return res.data;
}

export interface CommandDashboardActiveUsersMetrics {
  activeUsers: number;
  suspendedUsers: number;
  neverSignedInUsers: number;
  archivedUsers: number;
  /** Epoch millis, set by the server at request time - this endpoint is always live, never cached. */
  lastUpdated: number;
}

export async function getActiveUsersTileMetrics() {
  const res = await instance.get<CommandDashboardActiveUsersMetrics>(
    '/api/commanddashboard/activeusers'
  );
  return res.data;
}

/** A user's named, saved Command Dashboard tile arrangement. */
export interface SavedDashboardView {
  name: string;
  createdDate: number;
  color: string;
  tileRows: CommandDashboardTileKey[][];
}

/**
 * A user's Command Dashboard saved views. `activeView` is `"Default"` when the user hasn't
 * saved anything yet (or deleted their active view) - `"Default"` is never itself an entry in
 * `savedViews`.
 */
export interface DashboardView {
  activeView: string;
  savedViews: SavedDashboardView[];
  /**
   * What the grid should show when `activeView` has no matching entry in `savedViews` (i.e.
   * nothing has ever been saved) - the 3 seeded default tiles for an existing user, or empty for
   * a brand new one. Server-computed from isFirstGpanelLogin; empty for a real stored view, since
   * the matching `savedViews[].tileRows` is used in that case instead.
   */
  tileRows: CommandDashboardTileKey[][];
}

export async function getDashboardView() {
  const { data } = await instance.get<DashboardView>(
    '/api/commanddashboard/dashboardview'
  );
  return data;
}

export async function saveDashboardView(view: DashboardView) {
  const { data } = await instance.post<DashboardView>(
    '/api/commanddashboard/dashboardview',
    view
  );
  return data;
}

export const dashboardViewQuery = () => ({
  queryKey: [CommandDashboardKeys.DashboardView],
  queryFn: getDashboardView,
});

export type CommandDashboardTaskStatus = 'IDLE' | 'RUNNING' | 'COMPLETED';
export type CommandDashboardTaskStatuses = Record<
  string,
  CommandDashboardTaskStatus
>;

export async function getCommandDashboardTaskStatuses() {
  const { data } = await instance.get<CommandDashboardTaskStatuses>(
    '/api/commanddashboard/taskstatus'
  );
  return data;
}

/**
 * Running state for the task-backed tiles. Only those tiles observe this, so nothing is fetched
 * unless one of them is on the grid - pass `enabled` to keep it off when the tile has no
 * permission to read its own data either.
 */
export const commandDashboardTaskStatusesQuery = (enabled = true) =>
  queryOptions({
    queryKey: [CommandDashboardKeys.TaskStatuses],
    queryFn: getCommandDashboardTaskStatuses,
    enabled,
    // Both task-backed tiles share this one query, so the second one to mount reuses the first's
    // result instead of issuing its own request. Completion messages invalidate it explicitly.
    staleTime: 30_000,
    // A completion message is what normally ends a refresh. Polling only while something is
    // actually running covers the cases with no message: a dropped push, or the nightly dispatch,
    // which deliberately notifies nobody.
    refetchInterval: (query) =>
      Object.values(query.state.data ?? {}).includes('RUNNING')
        ? 30_000
        : false,
  });

export interface CommandDashboardLoginActivityDailyCounts {
  date: string;
  totalLogins: number;
  failedLogin: number;
  leakedPassword: number;
  suspiciousBlocked: number;
  genericDisable: number;
}

export interface CommandDashboardLoginActivitySnapshot {
  lastUpdated: number;
  dailyCounts: CommandDashboardLoginActivityDailyCounts[];
  truncated: boolean;
  // ISO yyyy-MM-dd "today" in the domain's own timezone, stamped fresh on every response - not
  // read back from storage. dailyCounts' chronologically-last entry is NOT reliably today's: right
  // after the nightly run (before any manual refresh that day) it's still yesterday's, since
  // "today" has zero elapsed time at midnight. Compare a bucket's date against this before
  // treating it as "today" for display.
  today: string;
}

export async function getLoginActivityTileMetrics() {
  const res = await instance.get<CommandDashboardLoginActivitySnapshot>(
    '/api/commanddashboard/loginactivity'
  );
  return res.data;
}

export async function refreshLoginActivityTileMetrics() {
  const res = await instance.post<void>(
    '/api/commanddashboard/loginactivity/refresh'
  );
  return res.data;
}

export type MasterSchedulerJobType =
  | 'report'
  | 'signature'
  | 'policy'
  | 'contactSync';

export type MasterSchedulerLastRunStatus =
  | 'PENDING'
  | 'RUNNING'
  | 'SUCCESSFUL'
  | 'FAILED';

export interface MasterSchedulerJob {
  jobType: MasterSchedulerJobType;
  jobId: string;
  jobName: string;
  /** Null when no next occurrence could be projected (e.g. an expired schedule). */
  nextRunTime: number | null;
  lastRunStatus: MasterSchedulerLastRunStatus;
}

export interface CommandDashboardMasterSchedulerSnapshot {
  lastUpdated?: number;
  jobs?: MasterSchedulerJob[];
  successfulJobs?: number;
  jobsCompletedToday?: number;
  jobsFailedLast24h?: number;
  jobsCurrentlyRunning?: number;
}

export async function getMasterSchedulerTileMetrics() {
  const res = await instance.get<CommandDashboardMasterSchedulerSnapshot>(
    '/api/commanddashboard/masterscheduler'
  );
  return res.data;
}

export interface CommandDashboardGroupsAndAccessMetrics {
  lastUpdated: number;
  totalGroups: number;
  totalSharedDrives: number;
  groupsWithNoOwner: number;
  groupsWithExternalMembers: number;
  sharedDrivesWithNoManager: number;
}

export async function getGroupsAndAccessTileMetrics() {
  const res = await instance.get<CommandDashboardGroupsAndAccessMetrics>(
    '/api/commanddashboard/groups'
  );
  return res.data;
}

export async function refreshGroupsAndAccessTileMetrics() {
  const res = await instance.post<void>('/api/commanddashboard/groups/refresh');
  return res.data;
}
